import { useState, useMemo, useCallback } from 'react'
import { cn } from '@/lib/utils'
import type { ProductOption, ProductVariant } from '@/types/database'

interface VariantSelectorProps {
  options: ProductOption[]
  variants: ProductVariant[]
  selectedVariantId: string | null
  onVariantChange: (variant: ProductVariant | null) => void
  className?: string
}

export function VariantSelector({
  options,
  variants,
  selectedVariantId,
  onVariantChange,
  className,
}: VariantSelectorProps) {
  // Track selected value per option axis
  const [selections, setSelections] = useState<Record<string, string>>(() => {
    // Initialize from selected variant if one is already chosen
    if (selectedVariantId) {
      const variant = variants.find(v => v.id === selectedVariantId)
      if (variant) return { ...variant.option_values }
    }
    return {}
  })

  // Resolve selected variant from current selections
  const resolvedVariant = useMemo(() => {
    if (Object.keys(selections).length < options.length) return null
    return variants.find(v =>
      options.every(opt => v.option_values[opt.name] === selections[opt.name])
    ) || null
  }, [selections, options, variants])

  // Check if a particular option value is available given current other selections
  const isValueAvailable = useCallback((optionName: string, value: string) => {
    const testSelections = { ...selections, [optionName]: value }
    // Check if any variant matches all selected options
    return variants.some(v =>
      v.available &&
      Object.entries(testSelections).every(([k, val]) => v.option_values[k] === val)
    )
  }, [selections, variants])

  const handleSelect = (optionName: string, value: string) => {
    const newSelections = { ...selections, [optionName]: value }
    setSelections(newSelections)

    // If all options are selected, resolve the variant
    if (Object.keys(newSelections).length >= options.length) {
      const variant = variants.find(v =>
        options.every(opt => v.option_values[opt.name] === newSelections[opt.name])
      ) || null
      onVariantChange(variant)
    } else {
      onVariantChange(null)
    }
  }

  if (options.length === 0) return null

  return (
    <div className={cn('space-y-4', className)}>
      {options
        .sort((a, b) => a.position - b.position)
        .map(option => (
          <div key={option.id}>
            <label className="mb-2 block text-sm font-medium text-zinc-700">
              {option.name}
              {selections[option.name] && (
                <span className="ml-2 font-normal text-zinc-500">
                  {selections[option.name]}
                </span>
              )}
            </label>
            <div className="flex flex-wrap gap-2">
              {option.values.map(value => {
                const isSelected = selections[option.name] === value
                const available = isValueAvailable(option.name, value)
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleSelect(option.name, value)}
                    disabled={!available}
                    className={cn(
                      'rounded-lg border px-4 py-2 text-sm font-medium transition-all',
                      isSelected
                        ? 'border-red-600 bg-red-50 text-red-700 ring-1 ring-red-600'
                        : available
                          ? 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400'
                          : 'cursor-not-allowed border-zinc-100 bg-zinc-50 text-zinc-300 line-through'
                    )}
                  >
                    {value}
                  </button>
                )
              })}
            </div>
          </div>
        ))}

      {resolvedVariant && !resolvedVariant.available && (
        <p className="text-sm text-red-600">This variant is currently unavailable.</p>
      )}
    </div>
  )
}
